import type { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { ROAD_PK_MESH } from '../gql/queries/geoPositions/geoPositionRoads';
import { getSeedKey } from '../gql/cacheSeed';
import { hydrateAndAgeRoadMeshes, saveRoadMeshes, RoadMesh } from './roadMeshStore';

/**
 * Cada cuánto se vuelve a bajar la cartografía.
 *
 * Es cartografía: no cambia de un día para otro. Y esto corre en CADA arranque,
 * así que pedirla siempre sería gastar los datos de la tablet para recibir lo
 * mismo. Una semana es el equilibrio: si se retoca la malla, la flota se entera
 * sola en unos días sin que nadie haga nada.
 */
export const MESH_MAX_AGE_DAYS = 7;

/**
 * Deja la malla de carreteras lista para usar sin cobertura.
 *
 * ── Por qué al arrancar y no cuando hace falta ─────────────────────────────
 *
 * Cuando hace falta ya es tarde: el operario está en un túnel o en un tramo sin
 * señal, que es justo donde el PK por GPS tiene que funcionar. La cartografía
 * se baja mientras HAY red y se queda en la tablet.
 *
 * ── Cómo se comporta ───────────────────────────────────────────────────────
 *
 * 1. Hidrata SIEMPRE lo que hubiera guardado. Es lo primero que pasa, y es lo
 *    que hace que un arranque sin cobertura siga teniendo malla.
 * 2. Si la copia es reciente, no pide nada más.
 * 3. Si es vieja o no hay, descarga y guarda.
 *
 * Nunca lanza y nunca bloquea la navegación: se llama fire-and-forget desde el
 * loader del layout. Sin red se sigue con lo hidratado, que es exactamente lo
 * que el `Map` en memoria de antes no podía ofrecer.
 */
export async function ensureRoadMesh(
  client: ApolloClient<unknown>
): Promise<void> {
  const ownerKey = getSeedKey();
  // Sin identidad no se sabe de quién sería la copia. Mejor no escribir: las
  // tablets se comparten.
  if (!ownerKey) return;

  // Hidrata y devuelve la edad de UNA lectura: preguntarlo por separado traía
  // la malla entera del disco dos veces en cada arranque.
  const edad = await hydrateAndAgeRoadMeshes(ownerKey);
  if (edad !== null && edad < MESH_MAX_AGE_DAYS) return;

  try {
    const { data } = await client.query<{ roadPkMesh: RoadMesh[] }>({
      query: ROAD_PK_MESH,
      fetchPolicy: 'network-only',
    });
    // Una respuesta vacía NO se guarda: un backend que falla y contesta `[]`
    // no debe dejar a la tablet sin cartografía.
    if (data?.roadPkMesh?.length) {
      await saveRoadMeshes(ownerKey, data.roadPkMesh);
    }
  } catch (err) {
    // Sin red se sigue con lo hidratado. No es un error del usuario ni algo
    // que deba interrumpir el arranque; se registra para poder ver si una
    // flota entera se queda sin bajarla.
    Sentry.captureException(err, {
      tags: { feature: 'road-mesh', step: 'prefetch' },
      level: 'warning',
    });
  }
}

export default ensureRoadMesh;
